/**
 * Document entity model class
 *
 * This file is part of ePlan
 * (c) Kaden und Partner AG
 */
import { BaseModel } from './BaseModel';
import { BaseCollection } from './BaseCollection';
import type { Dayjs } from 'dayjs';
import type { MutationsObject } from '@kp/js-model';
import dayjs from 'dayjs';
import type { User } from './User';
import { ref, type Ref } from 'vue';
import { instance as api } from '@/helpers/api';

const documentConfig: Ref<{ [key: string]: any }> = ref({});
let configLoaded = false;
export function useDocumentConfig() {
    if (!configLoaded) {
        documentConfig.value = {};
        configLoaded = true;
        api()
            .post('/Document/getConfig')
            .then((res) => {
                if (res.data?.config) {
                    documentConfig.value = res.data.config;
                }
            });
    }
    return documentConfig;
}

export class Document extends BaseModel {
    id: number | null = null;
    path: number | null = null;
    filename: number | null = null;
    filename_original: number | null = null;
    parent_entity: string | null = null;
    parent_id: number | null = null;
    parent_context: string | null = null;
    created: Dayjs | null = null;
    creator_id: number | null = null;
    mime: string | null = null;
    size: number | null = null;
    external_id: number | null = null;
    source: string | null = null;
    decision_ruling_id: number | null = null;
    deleted: boolean | null = null;
    sub_context: string | null = null;
    uuid: string | null = null;
    parent_document_id: number | null = null;
    version_nr: number | null = null;
    signed: boolean | null = null;
    modifed: Dayjs | null = null;
    modifier_id: number | null = null;
    internal: boolean | null = null;

    //view fields

    public mutations(): MutationsObject {
        return {
            id: (id) => (Number(id) > 0 ? Number(id) : null),
            parent_id: (id) => (Number(id) > 0 ? Number(id) : null),
            creator_id: (id) => (Number(id) > 0 ? Number(id) : null),
            created: (value) => dayjs(value)
        };
    }

    getClassName(): string {
        return 'Document';
    }

    getParentContexts() {
        return [
            'bericht',
            'begleitschreiben',
            'decision_ruling',
            'energietechnische_nachweise',
            'erdarbeiten',
            'ergaenzende_berichte',
            'ergaenzende_plaene',
            'ergaenzende_unterlagen',
            'formulare',
            'plan',
            'planungsbericht',
            'projektunterlagen',
            'rechtsverbindliche_berichte',
            'rechtsverbindliche_plaene',
            'richtungsweisende_inhalte',
            'shelter_control',
            'situationsplan_katasterplan',
            'vorschriften_reglement',
            'weitere_unterlagen',
            'zustimmung_dritter'
        ];
    }

    getParentPGContexts() {
        return [
            'ergaenzende_berichte',
            'ergaenzende_plaene',
            'fotos',
            'rechtsverbindliche_berichte',
            'rechtsverbindliche_plaene',
            'richtungsweisende_inhalte',
            'tabelle',
            'vertraege',
            'vorschriften',
            'weitere_unterlagen'
        ];
    }

    getSubContexts() {
        return [
            'baugesuchformular',
            'betrieb_mit_plangenehmigungspflicht',
            'energienachweise',
            'brandschutz',
            'entwaesserung',
            'behig',
            'industrie_und_gewerbe',
            'ls_hlkk',
            'ls_waermepumpen',
            'nachweis_berechnung_nutzungsziffer',
            'schadstoffabklaerung',
            'stellplaetze',
            'terrainveraenderungen'
        ];
    }

    getAllowedParentContexts(user: User) {
        const contexts = this.getParentContexts();
        let not_allowed_contexts: string[] = [];

        if (user.isRoleAdmin) {
            not_allowed_contexts = [];
        } else if (user.isRoleGemeinde) {
            not_allowed_contexts = [];
        } else if (user.isRoleFachstelle) {
            not_allowed_contexts = [];
        } else {
            not_allowed_contexts = ['shelter_control'];
        }

        return contexts.filter((context) => not_allowed_contexts.indexOf(context) === -1);
    }

    getAllowedParentPGContexts(user: User) {
        const contexts = this.getParentPGContexts();
        return contexts;
    }

    getAllowedSubContexts(user: User) {
        return this.getSubContexts();
    }
}

export class DocumentCollection extends BaseCollection<Document> {
    protected modelCls = Document;

    public getModelClassName(): string {
        return 'Document';
    }
}
